import './ResearcherFilter.sass';

import React from 'react';
import PropTypes from 'prop-types';
import { ArrowSquareOutIcon, XIcon } from '@phosphor-icons/react';
import { trackEvent as trackAnalyticsEvent } from 'modules/analytics';

import FilterSet from 'components/Explorer/models/filter_set';
import ModalPicker from 'components/Explorer/AdvancedSearch/ModalPicker';

// ResearcherFilter is a thin consumer of the shared ModalPicker, but several
// of the render-prop slots below are doing extra work to preserve the
// pre-migration researcher modal exactly: hardcoded English strings instead
// of i18n where the originals were hardcoded, an inline selected-list
// (h2 heading + empty-state paragraph) instead of the chevron-collapsed
// toggle the other filters use, and a bolded-term no-results message.
//
// HARMONISATION TODO (deferred):
//   1. Drop the renderSelectedList prop below — the default SelectedList
//      gives researcher the same shape as affiliation/funder.
//   2. Drop the renderNoResults prop — let the i18n no_results key drive it
//      (re-add `no_results`, `counter`, `counter_label` keys to
//      researchers.modal in config/locales/new-ui/en.yml).
//   3. Update the researchers.* strings in en.yml per the comment block
//      there (singular trigger labels, sentence case, proper pluralisation).
// After those three steps the consumer is six lines of config.

const ResearcherProfileLink = ({ id }) => (
  <a
    href={`https://app.dimensions.ai/details/entities/publication/author/${id}`}
    className="ResearcherFilter-profileLink"
    onClick={() =>
      trackAnalyticsEvent('Advanced Search: Modal Dialogue View Profile click', { dialogue_name: 'Researcher' })
    }
    target="_blank"
    rel="noreferrer"
  >
    View profile <ArrowSquareOutIcon size="16px" aria-hidden />
  </a>
);

ResearcherProfileLink.propTypes = {
  id: PropTypes.string.isRequired
};

const renderResearcherMeta = (item) =>
  item.affiliation && <span className="ResearcherFilter-affiliation">{item.affiliation}</span>;

// Each result row gets the publication count rendered as a bold, right-aligned
// line above the profile link. The visual treatment is preserved from CAP-1405
// even though the affiliation and funder pickers now render counts as a bare
// formatted number on the same line as the org name; harmonising the two
// presentations is deferred.
const renderResearcherExtras = (item) => {
  const count = item.altmetric_outputs_count;
  return (
    <span className="ResearcherFilter-resultMeta">
      {Number.isFinite(count) && (
        <span className="ResearcherFilter-outputsCount">
          {count} {count === 1 ? 'output' : 'outputs'}
        </span>
      )}
      <ResearcherProfileLink id={item.id} />
    </span>
  );
};

// Pre-migration no-results message: term is bolded, en-dash separator. Drop
// this prop and add the standard `modal.no_results` i18n key during the
// harmonisation work.
const renderResearcherNoResults = (term) => (
  <>
    No results found for <strong>{term}</strong> – try searching for something else.
  </>
);

// Pre-migration inline selected list: h2 count heading, explanatory empty
// paragraph, per-item profile link + remove button. Drop this prop during
// the harmonisation work so researcher uses the same chevron-
// collapsed SelectedList as affiliation and funder.
const renderResearcherSelectedList = ({ selected, onRemove }) => (
  <div className="ResearcherFilter-selectedSection">
    {/* One string, so React replaces the heading's text rather than updating text nodes a page translator has swapped out */}
    <h2 className="ResearcherFilter-heading">
      {`${selected.length} Selected Researcher${selected.length === 1 ? '' : 's'}`}
    </h2>
    {selected.length === 0 && (
      <p className="ResearcherFilter-empty">
        No researchers selected. Use the search above to find and add researchers.
      </p>
    )}
    {selected.length > 0 && (
      <ul className="ResearcherFilter-selection">
        {selected.map((item) => (
          <li key={item.id} className="ResearcherFilter-selectedItem">
            <span className="ResearcherFilter-selectedItemName">{item.name}</span>
            <ResearcherProfileLink id={item.id} />
            <button type="button" className="ResearcherFilter-removeItem" onClick={() => onRemove(item)}>
              <XIcon size="16px" aria-hidden />
              <span className="visually-hidden">Remove {item.name} from selection</span>
            </button>
          </li>
        ))}
      </ul>
    )}
  </div>
);

// The researcher autocomplete returns an envelope rather than a bare array:
// { organisation_name, researchers }, where each researcher carries a
// same_organisation flag. Unwrap it into the results array plus the org name
// so the rows render as usual and groupResearcherResults can label the sections.
const parseResearcherResponse = (body) => ({
  results: body?.researchers ?? [],
  meta: { organisationName: body?.organisation_name ?? null }
});

// Float researchers from the logged-in user's own organisation into a leading
// "From [organisation]" section, above an "Other results" section. The backend
// already orders org matches first, so partitioning preserves their leading
// position and the rows keep their keyboard-navigation index. Falls back to a
// flat list (return null) when the institution has no resolved organisation
// name or nothing matched it — the no-GRID / no-colleague case. Section labels
// are hardcoded English, in line with the rest of this copy-preserving consumer.
const groupResearcherResults = (rows, meta) => {
  if (!meta?.organisationName) return null;

  const orgRows = rows.filter((row) => row.item.same_organisation);
  if (orgRows.length === 0) return null;

  const otherRows = rows.filter((row) => !row.item.same_organisation);
  const groups = [{ key: 'organisation', label: `From ${meta.organisationName}`, rows: orgRows }];
  if (otherRows.length > 0) groups.push({ key: 'other', label: 'Other results', rows: otherRows });

  return groups;
};

const ResearcherFilter = ({ filterSet }) => (
  <ModalPicker
    filterSet={filterSet}
    i18nKey="researchers"
    filterKey="researcher_id[]"
    autocompletePath="/explorer/autocomplete/researchers"
    bootstrapMethod="researchers"
    analyticsDialogueName="Researcher"
    maxSelected={null}
    helperText={[
      I18n.t('Explorer.AdvancedSearch.fields.researchers.modal.helper_name'),
      I18n.t('Explorer.AdvancedSearch.fields.researchers.modal.helper_source'),
      I18n.t('Explorer.AdvancedSearch.fields.researchers.modal.helper_count')
    ]}
    renderResultMeta={renderResearcherMeta}
    renderResultExtras={renderResearcherExtras}
    renderNoResults={renderResearcherNoResults}
    renderSelectedList={renderResearcherSelectedList}
    parseResponse={parseResearcherResponse}
    getResultGroups={groupResearcherResults}
  />
);

ResearcherFilter.propTypes = {
  filterSet: PropTypes.instanceOf(FilterSet).isRequired
};

export default ResearcherFilter;
